// =====================================================
// PROFESSIONAL TRADING JOURNAL SERVICE
// =====================================================
// Handles parsing AI responses, saving journal entries, and retrieving stats

import { supabase } from '@/integrations/supabase/client';
import type {
  JournalEntry,
  JournalEntryRow,
  CreateJournalEntryInput,
  UpdateOutcomeInput,
  JournalStats,
  JournalFilters,
  AIDecision,
  ExecutionPermission,
  Direction,
  rowToJournalEntry,
} from '@/types/journal';
import { 
  createCounterfactualTracking, 
  shouldTrackCounterfactual,
  estimateStopLoss 
} from './counterfactualService';

// =====================================================
// INTELLIGENT MARKET BIAS DETECTION
// =====================================================

/**
 * Detect market bias from AI analysis text for NO_TRADE/WAIT decisions
 * Parses keywords to determine if the market sentiment was bearish or bullish
 * This enables accurate counterfactual tracking even when no explicit direction is given
 */
function detectMarketBiasFromAnalysis(
  explicitDirection: Direction | null | undefined,
  analysisText: string | null | undefined,
  marketRegime: string | null | undefined
): 'LONG' | 'SHORT' {
  // If explicit direction is provided and valid, use it
  if (explicitDirection && explicitDirection !== 'NONE') {
    return explicitDirection as 'LONG' | 'SHORT';
  }

  // If no analysis text, check market regime
  if (!analysisText) {
    if (marketRegime) {
      const regime = marketRegime.toLowerCase();
      if (regime.includes('risk-off') || regime.includes('bearish') || regime.includes('distribution')) {
        return 'SHORT';
      }
    }
    return 'LONG'; // Default fallback
  }

  const text = analysisText.toLowerCase();

  // Bearish keywords - suggest SHORT bias
  const bearishKeywords = [
    'bearish', 'downside risk', 'resistance', 'overbought', 'blow-off', 'exhaustion',
    'profit-taking', 'distribution', 'risk-off', 'macro risk', 'overextended',
    'overextension', 'selling pressure', 'breakdown', 'lower high', 'death cross',
    'head and shoulders', 'double top', 'triple top', 'rising wedge', 'bearish divergence',
    'negative', 'decline', 'weakness', 'deteriorating', 'failing', 'rejection',
    'excess leverage', 'liquidity sweep down', 'stop hunt below', 'shorts covering'
  ];

  // Bullish keywords - suggest LONG bias
  const bullishKeywords = [
    'bullish', 'upside potential', 'support', 'oversold', 'accumulation',
    'buy pressure', 'breakout', 'higher low', 'golden cross', 'risk-on',
    'double bottom', 'triple bottom', 'falling wedge', 'bullish divergence',
    'positive', 'strength', 'improving', 'recovery', 'bounce',
    'liquidity sweep up', 'stop hunt above', 'shorts squeezed', 'dip buying'
  ];

  // Count keyword matches
  let bearishScore = 0;
  let bullishScore = 0;

  for (const keyword of bearishKeywords) {
    if (text.includes(keyword)) {
      bearishScore++;
    }
  }

  for (const keyword of bullishKeywords) {
    if (text.includes(keyword)) {
      bullishScore++;
    }
  }

  // Special patterns that strongly indicate direction
  if (text.includes('no trade') && text.includes('downside')) bearishScore += 3;
  if (text.includes('no trade') && text.includes('upside')) bullishScore += 3;
  if (text.includes('excess leverage') || text.includes('blow-off')) bearishScore += 2;
  if (text.includes('capitulation') || text.includes('panic sell')) bullishScore += 2; // Often signals bottom
  
  // Market regime strong signals
  if (marketRegime) {
    const regime = marketRegime.toLowerCase();
    if (regime.includes('risk-off') || regime.includes('bearish')) bearishScore += 3;
    if (regime.includes('risk-on') || regime.includes('bullish') || regime.includes('trend')) bullishScore += 3;
  }

  console.log(`📊 [BiasDetection] Bearish: ${bearishScore}, Bullish: ${bullishScore}`);

  // Return direction based on scores
  if (bearishScore > bullishScore) {
    return 'SHORT';
  }
  
  return 'LONG'; // Default to LONG if equal or bullish
}

// =====================================================
// AI RESPONSE PARSER
// =====================================================

/**
 * Parse AI analysis text to extract trading decision and levels
 * Supports the new EXECUTION_DECISION_BLOCK format for machine-readable decisions
 * Phase 3: Also parses INSTITUTIONAL_REVIEW block
 */
export function parseAIAnalysis(text: string): AIDecision {
  if (!text || typeof text !== 'string') {
    return getDefaultDecision();
  }

  // =========================================================
  // STEP 1: Parse EXECUTION_DECISION_BLOCK (Primary - Machine-readable)
  // This is the authoritative source of truth for all decisions
  // =========================================================
  const blockMatch = text.match(/━+\s*EXECUTION_DECISION_BLOCK\s*━+[\s\S]*?━+/i);
  
  // =========================================================
  // STEP 1.5: Parse INSTITUTIONAL_REVIEW block (Phase 3)
  // =========================================================
  const reviewMatch = text.match(/━+\s*INSTITUTIONAL_REVIEW\s*━+[\s\S]*?━+/i);
  let riskOfficerStance: 'APPROVE' | 'VETO' | 'CAUTION' | null = null;
  let capitalProtectionScore: number | null = null;
  let restraintReason: string | null = null;
  let marketAlignment: 'Aligned' | 'Misaligned' | 'Neutral' | null = null;
  let disciplineAssessment: 'Excellent' | 'Good' | 'Acceptable' | 'Warning' | 'Critical' | null = null;
  
  if (reviewMatch) {
    const review = reviewMatch[0];
    
    // Risk Officer Stance
    const stanceMatch = review.match(/Risk_Officer_Stance:\s*(APPROVE|VETO|CAUTION)/i);
    if (stanceMatch) {
      riskOfficerStance = stanceMatch[1].toUpperCase() as any;
    }
    
    // Capital Protection Score
    const cpsMatch = review.match(/Capital_Protection_Score:\s*(\d+)/i);
    if (cpsMatch) {
      capitalProtectionScore = parseInt(cpsMatch[1]);
    }
    
    // Restraint Reason
    const restraintMatch = review.match(/Restraint_Reason:\s*\[?([^\]\n]+)\]?/i);
    if (restraintMatch) {
      restraintReason = restraintMatch[1].trim();
    }
    
    // Market Alignment
    const alignMatch = review.match(/Market_Alignment:\s*(Aligned|Misaligned|Neutral)/i);
    if (alignMatch) {
      marketAlignment = alignMatch[1] as any;
    }
    
    // Discipline Assessment
    const discMatch = review.match(/Discipline_Assessment:\s*(Excellent|Good|Acceptable|Warning|Critical)/i);
    if (discMatch) {
      disciplineAssessment = discMatch[1] as any;
    }
  }
  
  if (blockMatch) {
    const block = blockMatch[0];
    
    // Extract Decision
    const decisionMatch = block.match(/Decision:\s*(TRADE_APPROVED|NO_TRADE|WAIT|CONDITIONAL)/i);
    const decision = decisionMatch ? decisionMatch[1].toUpperCase() as ExecutionPermission : 'PENDING';
    
    // Extract Direction
    const dirMatch = block.match(/Direction:\s*(LONG|SHORT|NONE)/i);
    const direction = dirMatch ? dirMatch[1].toUpperCase() as Direction : 'NONE';
    
    // Extract Market Regime
    const regimeMatch = block.match(/Market_Regime:\s*(Trend|Range|Distribution|Accumulation|Risk-Off|Volatility-Expansion)/i);
    const marketRegime = regimeMatch ? regimeMatch[1] : null;
    
    // Extract Primary Reason
    const reasonMatch = block.match(/Primary_Reason:\s*\[?([^\]\n]+)\]?/i);
    const primaryReason = reasonMatch ? reasonMatch[1].trim() : null;
    
    // Extract Invalidation Level
    const invalidMatch = block.match(/Invalidation_Level:\s*\[?([^\]\n]+)\]?/i);
    const invalidationLevel = invalidMatch ? invalidMatch[1].trim() : null;
    
    // Extract Review Trigger
    const reviewMatch = block.match(/Review_Trigger:\s*\[?([^\]\n]+)\]?/i);
    const reviewTrigger = reviewMatch ? reviewMatch[1].trim() : null;
    
    // Extract price levels from block (handles ranges like "$67,000 – $67,200")
    const extractBlockPrice = (keys: string[]): number | null => {
      for (const key of keys) {
        // Try exact single price first
        const singlePattern = new RegExp(`${key}:\\s*\\*{0,2}\\s*\\$?([0-9,]+(?:\\.[0-9]+)?)`, 'i');
        const singleMatch = block.match(singlePattern);
        if (singleMatch) {
          const val = parseFloat(singleMatch[1].replace(/,/g, ''));
          if (!isNaN(val) && val > 0) return val;
        }
      }
      return null;
    };
    
    const entry = extractBlockPrice(['Entry_Price', 'Entry_Zone', 'Entry']);
    const stopLoss = extractBlockPrice(['Stop_Loss', 'SL']);
    const tp1 = extractBlockPrice(['Take_Profit_1', 'TP1']);
    const tp2 = extractBlockPrice(['Take_Profit_2', 'TP2']);
    const tp3 = extractBlockPrice(['Take_Profit_3', 'TP3']);
    
    // Extract Risk:Reward
    const rrMatch = block.match(/Risk_Reward:\s*([0-9.]+)/i);
    const riskReward = rrMatch ? parseFloat(rrMatch[1]) : null;
    
    // Extract Confidence
    const confMatch = block.match(/Confidence:\s*([0-9]+)/i);
    const confidence = confMatch ? parseInt(confMatch[1]) : null;
    
    // For non-trade decisions, preserve the direction bias for counterfactual tracking
    if (decision === 'NO_TRADE' || decision === 'WAIT') {
      return {
        executionPermission: decision,
        direction: direction || 'NONE', // Keep the suggested direction for counterfactual tracking
        entry: entry,
        stopLoss: stopLoss,
        takeProfit1: tp1,
        takeProfit2: tp2,
        takeProfit3: tp3,
        riskReward: riskReward,
        confidence: confidence,
        marketRegime: marketRegime as any,
        primaryReason,
        invalidationLevel,
        reviewTrigger,
        // Phase 3: Institutional Review
        riskOfficerStance,
        capitalProtectionScore,
        restraintReason,
        marketAlignment,
        disciplineAssessment,
      };
    }
    
    // For CONDITIONAL, include the review trigger as the condition
    if (decision === 'CONDITIONAL') {
      return {
        executionPermission: 'CONDITIONAL',
        direction,
        entry,
        stopLoss,
        takeProfit1: tp1,
        takeProfit2: tp2,
        takeProfit3: tp3,
        riskReward,
        confidence,
        marketRegime: marketRegime as any,
        primaryReason,
        invalidationLevel,
        reviewTrigger,
        // Phase 3: Institutional Review
        riskOfficerStance,
        capitalProtectionScore,
        restraintReason,
        marketAlignment,
        disciplineAssessment,
      };
    }
    
    // TRADE_APPROVED - normalize to 'TRADE' for backward compatibility
    return {
      executionPermission: 'TRADE',
      direction,
      entry,
      stopLoss,
      takeProfit1: tp1,
      takeProfit2: tp2,
      takeProfit3: tp3,
      riskReward,
      confidence,
      marketRegime: marketRegime as any,
      primaryReason,
      invalidationLevel,
      reviewTrigger,
      // Phase 3: Institutional Review
      riskOfficerStance,
      capitalProtectionScore,
      restraintReason,
      marketAlignment,
      disciplineAssessment,
    };
  }
  
  // =========================================================
  // STEP 2: Enhanced Fallback - Check for TRADE AUTHORIZED patterns
  // Improved to handle free-text responses without structured blocks
  // =========================================================
  const upperText = text.toUpperCase();
  let executionPermission: ExecutionPermission = 'PENDING';
  let direction: Direction | null = null;
  
  // Try to find the Final Trade Authorization section
  const finalAuthMatch = text.match(/#+\s*\d*\.?\s*Final Trade Authorization[\s\S]*?(?=#+\s*\d|$)/i);
  
  // Enhanced patterns to match various AI response formats
  const tradeAuthorizedPatterns = [
    /✅\s*\*?\*?TRADE\s*AUTHORIZED\*?\*?/i,
    /TRADE\s*AUTHORIZED\s*[—–-]?\s*(LONG|SHORT)?/i,
    /EXECUTION\s*AUTHORIZED/i,
    /EXECUTION\s*PERMISSION:\s*YES/i,
    /GO\s*(LONG|SHORT)/i,
    /POSITION:\s*(LONG|SHORT)/i,
  ];
  
  // NO_TRADE patterns - check these BEFORE trade patterns in Final Authorization section
  const noTradePatterns = [
    /❌\s*\*?\*?NO[_\s-]?TRADE\*?\*?/i,
    /⛔\s*NO[_\s-]?TRADE/i,
    /🚫\s*NO[_\s-]?TRADE/i,
    /NO[_\s-]?TRADE\s*[—–-]\s*(STRUCTURE|CONDITIONS|SETUP)/i,
    /STRUCTURE\s*NOT\s*READY/i,
    /DO\s*NOT\s*TRADE/i,
    /STAND\s*ASIDE/i,
    /NO\s*VALID\s*SETUP/i,
    /INVALID\s*SETUP/i,
    /EXECUTION\s*PERMISSION:\s*NO/i,
    /TRADE\s*NOT\s*AUTHORIZED/i,
  ];
  
  const waitPatterns = [
    /⏳\s*\*?\*?WAIT\*?\*?/i,
    /WAIT\s*[—–\-−]+\s*STRUCTURE/i,  // Added − (minus sign) to character class
    /WAIT\s*[—–\-−]+\s*CONDITIONS/i,
    /AWAIT\s*CONFIRMATION/i,
    /WAIT\s*FOR\s*CONFIRMATION/i,
    /WAIT\s+[—–\-−]/i,              // Match WAIT followed by any dash variant
    /WAIT\s+—/i,                     // Explicit em-dash
    /WAIT\s+–/i,                     // Explicit en-dash
    /WAIT\s+-/i,                     // Explicit hyphen
    /WATCH\s*[—–\-−]+\s*MONITORING/i, // WATCH - MONITORING alias for WAIT
    /WATCH\s*[—–\-−]+\s*OBSERVE/i,    // WATCH - OBSERVE alias for WAIT
  ];
  
  // Check in both the full text and the final auth section
  const searchText = finalAuthMatch ? finalAuthMatch[0] : text;
  
  // Debug log for troubleshooting
  console.log('🔍 Journal parsing - checking for decision patterns in text length:', text.length);
  
  // =====================================================
  // IMPORTANT: Check WAIT/NO_TRADE FIRST because they are explicit decisions
  // Only fall back to TRADE patterns if no explicit refusal is found
  // =====================================================
  
  // Check WAIT patterns FIRST (most specific - explicit wait decisions)
  const hasWaitStructure = /WAIT\s*[—–\-−]+\s*STRUCTURE/i.test(searchText) || 
                           (searchText.toUpperCase().includes('WAIT') && searchText.toUpperCase().includes('STRUCTURE NOT'));
  
  const hasExplicitWait = /⏳\s*WAIT/i.test(searchText) || 
                          /WATCH\s*[—–\-−]+\s*MONITORING/i.test(searchText) ||
                          waitPatterns.some(pattern => pattern.test(searchText));
  
  if (hasWaitStructure || hasExplicitWait) {
    executionPermission = 'WAIT';
    direction = 'NONE';
    console.log('✅ Detected WAIT decision via WAIT patterns');
  } 
  // Then check NO_TRADE patterns (but not if it's just in a "No-Trade Condition:" context)
  else if (noTradePatterns.some(pattern => pattern.test(searchText))) {
    // Double-check this isn't just a "No-Trade Condition" label
    const isJustConditionLabel = /No-Trade\s*Condition:/i.test(text) && 
                                  !/^\s*NO[_\s-]?TRADE\s*$/im.test(searchText);
    if (!isJustConditionLabel) {
      executionPermission = 'NO_TRADE';
      direction = 'NONE';
      console.log('✅ Detected NO_TRADE decision via noTradePatterns');
    }
  }
  
  // Only check TRADE patterns if no explicit WAIT/NO_TRADE found
  if (executionPermission === 'PENDING') {
    for (const pattern of tradeAuthorizedPatterns) {
      const match = searchText.match(pattern);
      if (match) {
        executionPermission = 'TRADE';
        // Extract direction from the match if available
        if (match[1]) {
          direction = match[1].toUpperCase() as Direction;
        }
        console.log('✅ Detected TRADE decision via tradeAuthorizedPatterns');
        break;
      }
    }
  }
  
  // =========================================================
  // STEP 3: Legacy fallback patterns - Enhanced WAIT detection
  // =========================================================
  if (executionPermission === 'PENDING') {
    // Check for WAIT patterns first (more specific)
    if (
      upperText.includes('WAIT') && 
      (upperText.includes('STRUCTURE NOT') || upperText.includes('— STRUCTURE') || upperText.includes('– STRUCTURE') || upperText.includes('- STRUCTURE'))
    ) {
      executionPermission = 'WAIT';
      direction = 'NONE';
      console.log('✅ Detected WAIT decision via legacy fallback (STRUCTURE pattern)');
    } else if (upperText.includes('⏳') && upperText.includes('WAIT')) {
      executionPermission = 'WAIT';
      direction = 'NONE';
      console.log('✅ Detected WAIT decision via legacy fallback (emoji pattern)');
    } else if (
      upperText.includes('NO-TRADE') || 
      upperText.includes('NO TRADE') ||
      upperText.includes('EXECUTION PERMISSION: NO') ||
      upperText.includes('EXECUTION PERMISSION:** NO')
    ) {
      executionPermission = 'NO_TRADE';
      direction = 'NONE';
    } else if (
      upperText.includes('CONDITIONAL') ||
      upperText.includes('WAIT FOR CONFIRMATION')
    ) {
      executionPermission = 'CONDITIONAL';
    } else if (
      upperText.includes('EXECUTION PERMISSION: YES') ||
      upperText.includes('EXECUTION PERMISSION:** YES') ||
      upperText.includes('TRADE AUTHORIZED')
    ) {
      executionPermission = 'TRADE';
    }
  }

  // =========================================================
  // STEP 4: Extract direction if not already set
  // =========================================================
  if (!direction) {
    // Extract the trade setup section (Section 5) for more targeted direction search
    const tradeSetupSection = text.match(/#+\s*\d*\.?\s*(?:Institutional\s*)?Trade\s*(?:Decision|Setup|Plan)[\s\S]*?(?=#+\s*\d|$)/i)?.[0] || '';
    const finalAuthSection = text.match(/#+\s*\d*\.?\s*Final\s*Trade\s*Authorization[\s\S]*?(?=#+\s*\d|$)/i)?.[0] || '';
    
    // Priority 1: Look in trade setup / final auth sections first (most reliable)
    const targetedSections = tradeSetupSection + '\n' + finalAuthSection;
    
    const directionPatterns = [
      /\*\*Direction\*\*:\s*\*{0,2}\s*(LONG|SHORT)/i,     // Gemini: **Direction:** **LONG**
      /Direction\*{0,2}:\s*\*{0,2}\s*(LONG|SHORT)/i,      // Direction:** **LONG**
      /Direction:\s*(LONG|SHORT)/i,                         // GPT: Direction: LONG
      /TRADE\s*AUTHORIZED\s*[—–-]+\s*\*{0,2}(LONG|SHORT)/i,
      /BIAS:\s*\*{0,2}\s*(LONG|SHORT|BULLISH|BEARISH)/i,
    ];
    
    // Try targeted sections first, then fall back to full text
    let matched = false;
    for (const searchArea of [targetedSections, text]) {
      if (matched) break;
      const searchClean = searchArea.replace(/\*\*/g, '');
      for (const pattern of directionPatterns) {
        const match = searchClean.match(pattern);
        if (match) {
          const dir = match[1].toUpperCase();
          direction = dir === 'BULLISH' ? 'LONG' : dir === 'BEARISH' ? 'SHORT' : dir as Direction;
          console.log(`✅ Direction detected: ${direction} (from ${searchArea === text ? 'full text' : 'trade section'})`);
          matched = true;
          break;
        }
      }
    }
  }
  
  // Set direction to NONE for non-trade decisions
  if (!direction && (executionPermission === 'NO_TRADE' || executionPermission === 'WAIT')) {
    direction = 'NONE';
  }
  
  // =========================================================
  // STEP 5: For NO_TRADE or WAIT, preserve direction for counterfactual
  // =========================================================
  if (executionPermission === 'NO_TRADE' || executionPermission === 'WAIT') {
    // Continue to extract direction and price levels for counterfactual tracking
    // Don't return early - fall through to extract the suggested direction
  }

  // Extract price levels
  const extractPrice = (patterns: RegExp[], preprocess?: (s: string) => string): number | null => {
    const searchText = preprocess ? preprocess(text) : text;
    for (const pattern of patterns) {
      const match = searchText.match(pattern);
      if (match) {
        const value = match[1].replace(/,/g, '');
        const num = parseFloat(value);
        if (!isNaN(num) && num > 0) return num;
      }
    }
    return null;
  };

  // Helper to strip markdown bold markers from text before price extraction
  const stripBold = (s: string) => s.replace(/\*\*/g, '');

  const entry = extractPrice([
    /Entry(?:\s*(?:Price)?)?[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
    /Entry[*]*:\s*\*{0,2}\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,              // Gemini: **Entry:** **$5,190**
    /Entry\s*Zone\*{0,2}[:\s]*\*{0,2}\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,  // Gemini: **Entry Zone**: **$66,050 – $66,250**
  ], stripBold);

  const stopLoss = extractPrice([
    /Stop Loss(?:\s*\(SL\))?[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
    /SL[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
  ], stripBold);

  const takeProfit1 = extractPrice([
    /Take Profit 1(?:\s*\(TP1\))?[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
    /TP1[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
  ], stripBold);

  const takeProfit2 = extractPrice([
    /Take Profit 2(?:\s*\(TP2\))?[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
    /TP2[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
  ], stripBold);

  const takeProfit3 = extractPrice([
    /Take Profit 3(?:\s*\(TP3\))?[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
    /TP3[*]*:\s*[*]*\s*\$?([0-9,]+(?:\.[0-9]+)?)/i,
  ], stripBold);

  // Extract Risk:Reward (strip bold for Gemini compatibility)
  let riskReward: number | null = null;
  const cleanText = stripBold(text);
  const rrPatterns = [
    /R(?:isk)?[:\s]*R(?:eward)?[:\s]*(?:Ratio)?[:\s]*1?[:\s]*([0-9.]+)/i,
    /Risk[:\s]*Reward[:\s]*(?:Ratio)?[:\s]*1?[:\s]*([0-9.]+)/i,
    /([0-9.]+)[:\s]*1\s*R/i,
  ];
  for (const pattern of rrPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      const val = parseFloat(match[1]);
      if (!isNaN(val) && val > 0) {
        riskReward = val;
        break;
      }
    }
  }

  // Extract Confidence
  let confidence: number | null = null;
  const confPatterns = [
    /(?:Overall\s*)?Confidence[:\s]*([0-9]+)%?/i,
    /Setup Confidence[:\s]*([0-9]+)%?/i,
  ];
  for (const pattern of confPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      const val = parseInt(match[1]);
      if (!isNaN(val) && val >= 0 && val <= 100) {
        confidence = val;
        break;
      }
    }
  }

  return {
    executionPermission,
    direction,
    entry,
    stopLoss,
    takeProfit1,
    takeProfit2,
    takeProfit3,
    riskReward,
    confidence,
  };
}

function getDefaultDecision(): AIDecision {
  return {
    executionPermission: 'PENDING',
    direction: null,
    entry: null,
    stopLoss: null,
    takeProfit1: null,
    takeProfit2: null,
    takeProfit3: null,
    riskReward: null,
    confidence: null,
  };
}

// =====================================================
// JOURNAL ENTRY OPERATIONS
// =====================================================

/**
 * Save a new journal entry from an AI analysis
 * Called automatically after each analysis
 */
export async function saveJournalEntry(input: CreateJournalEntryInput): Promise<JournalEntry | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('❌ Cannot save journal entry: User not authenticated');
      return null;
    }

    // Parse the AI analysis
    const aiDecision = parseAIAnalysis(input.analysisText);
    
    console.log('📝 Saving journal entry:', {
      asset: input.assetSymbol,
      market: input.marketType,
      decision: aiDecision.executionPermission,
      direction: aiDecision.direction,
    });

    // Determine if this is a non-trade decision (NO_TRADE or WAIT)
    const isNonTrade = aiDecision.executionPermission === 'NO_TRADE' || aiDecision.executionPermission === 'WAIT';
    
    // Ensure direction is never null - default to 'NONE' for non-trade or unknown decisions
    const safeDirection = aiDecision.direction || (isNonTrade ? 'NONE' : null);
    
    // Log warning if parsing returned PENDING (pattern not matched)
    if (aiDecision.executionPermission === 'PENDING') {
      console.warn('⚠️ AI analysis did not match any known decision pattern, saving as PENDING');
    }
    
    const row: Partial<JournalEntryRow> = {
      user_id: user.id,
      market_type: input.marketType,
      asset_symbol: input.assetSymbol,
      session: input.session,
      input_price: input.inputPrice,
      execution_permission: aiDecision.executionPermission,
      direction: safeDirection,
      entry_price: aiDecision.entry,
      stop_loss: aiDecision.stopLoss,
      take_profit_1: aiDecision.takeProfit1,
      take_profit_2: aiDecision.takeProfit2,
      take_profit_3: aiDecision.takeProfit3,
      risk_reward: aiDecision.riskReward,
      confidence: aiDecision.confidence,
      raw_analysis_text: input.analysisText,
      model_used: input.modelUsed,
      chart_count: input.chartCount,
      request_id: `req_${Date.now()}`,
      // Phase 2.2: Structured justification fields from EXECUTION_DECISION_BLOCK
      market_regime: aiDecision.marketRegime || null,
      primary_reason: aiDecision.primaryReason || null,
      invalidation_level: aiDecision.invalidationLevel || null,
      review_trigger: aiDecision.reviewTrigger || null,
      // Auto-analyze uploads ALL chart images to trade-screenshots storage
      // and passes the returned paths as an array. screenshot_url mirrors
      // the first one for backwards compat with any code that still reads
      // the single-value column. Both columns are populated together.
      screenshot_url: input.screenshotUrl ?? input.screenshotUrls?.[0] ?? null,
      screenshot_urls: input.screenshotUrls && input.screenshotUrls.length > 0
        ? input.screenshotUrls
        : (input.screenshotUrl ? [input.screenshotUrl] : null),
      // Auto-close NO_TRADE and WAIT decisions immediately (capital preservation = success)
      outcome_status: isNonTrade 
        ? (aiDecision.executionPermission === 'WAIT' ? 'WAIT' : 'NO_TRADE') 
        : 'PENDING',
      outcome_r_multiple: isNonTrade ? 0 : null,
      outcome_updated_at: isNonTrade ? new Date().toISOString() : null,
    };

    const { data, error } = await supabase
      .from('journal_entries')
      .insert(row as any)
      .select()
      .single();

    if (error) {
      console.error('❌ Error saving journal entry:', error);
      return null;
    }

    console.log('✅ Journal entry saved:', data?.id);
    
    // Phase 4: Create counterfactual tracking for NO_TRADE/WAIT decisions
    if (shouldTrackCounterfactual(aiDecision.executionPermission) && data?.id && input.inputPrice) {
      // Intelligent direction detection: parse analysis text for market bias
      const direction = detectMarketBiasFromAnalysis(
        aiDecision.direction,
        input.analysisText,
        aiDecision.marketRegime
      );
      const hypotheticalSL = aiDecision.stopLoss || estimateStopLoss(input.inputPrice, direction, input.marketType);
      
      createCounterfactualTracking({
        journalEntryId: data.id,
        userId: user.id,
        priceAtDecision: input.inputPrice,
        directionIfTraded: direction,
        hypotheticalStopLoss: hypotheticalSL,
      }).then((tracking) => {
        if (tracking) {
          console.log('📊 Counterfactual tracking created:', tracking.id);
        }
      }).catch((err) => {
        console.warn('⚠️ Failed to create counterfactual tracking:', err);
      });
    }
    
    return rowToJournalEntryFn(data as JournalEntryRow);
  } catch (error) {
    console.error('❌ Error saving journal entry:', error);
    return null;
  }
}

/**
 * Get all journal entries for the current user with optional filters
 */
export async function getJournalEntries(filters?: JournalFilters): Promise<JournalEntry[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    let query = supabase
      .from('journal_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters?.dateTo) {
      query = query.lte('created_at', filters.dateTo + 'T23:59:59');
    }
    if (filters?.marketType && filters.marketType !== 'all') {
      query = query.eq('market_type', filters.marketType);
    }
    if (filters?.assetSymbol && filters.assetSymbol !== 'all') {
      query = query.eq('asset_symbol', filters.assetSymbol);
    }
    if (filters?.executionPermission && filters.executionPermission !== 'all') {
      query = query.eq('execution_permission', filters.executionPermission);
    }
    if (filters?.outcomeStatus && filters.outcomeStatus !== 'all') {
      query = query.eq('outcome_status', filters.outcomeStatus);
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching journal entries:', error);
      return [];
    }

    return (data || []).map((row: any) => rowToJournalEntryFn(row as JournalEntryRow));
  } catch (error) {
    console.error('❌ Error fetching journal entries:', error);
    return [];
  }
}

/**
 * Get a single journal entry by ID
 */
export async function getJournalEntry(id: string): Promise<JournalEntry | null> {
  try {
    const { data, error } = await supabase
      .from('journal_entries')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('❌ Error fetching journal entry:', error);
      return null;
    }

    return rowToJournalEntryFn(data as JournalEntryRow);
  } catch (error) {
    console.error('❌ Error fetching journal entry:', error);
    return null;
  }
}

/**
 * Update the outcome of a journal entry
 */
export async function updateJournalOutcome(id: string, outcome: UpdateOutcomeInput): Promise<boolean> {
  try {
    // First verify the user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('❌ No authenticated user found');
      return false;
    }
    console.log('📝 Authenticated user:', user.id);
    console.log('📝 Updating entry:', id);
    console.log('📝 New outcome:', outcome);

    // Ensure correct sign based on outcome status
    let adjustedRMultiple = outcome.outcomeRMultiple;
    if (adjustedRMultiple !== null && adjustedRMultiple !== undefined) {
      if (outcome.outcomeStatus === 'LOSS') {
        adjustedRMultiple = -Math.abs(adjustedRMultiple); // LOSS = always negative
      } else if (outcome.outcomeStatus === 'WIN') {
        adjustedRMultiple = Math.abs(adjustedRMultiple); // WIN = always positive
      }
    }

    // Perform update with explicit user_id check for RLS compatibility
    const { error: updateError } = await supabase
      .from('journal_entries')
      .update({
        outcome_status: outcome.outcomeStatus,
        outcome_r_multiple: adjustedRMultiple,
        outcome_notes: outcome.outcomeNotes,
        outcome_updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .eq('user_id', user.id); // Explicit user_id check

    if (updateError) {
      console.error('❌ Error updating journal outcome:', updateError);
      console.error('❌ Error details:', JSON.stringify(updateError, null, 2));
      return false;
    }

    // Verify the update worked by fetching the entry
    const { data: verifyData, error: verifyError } = await supabase
      .from('journal_entries')
      .select('id, outcome_status')
      .eq('id', id)
      .single();

    if (verifyError) {
      console.error('❌ Error verifying update:', verifyError);
      return false;
    }

    if (verifyData?.outcome_status !== outcome.outcomeStatus) {
      console.warn('⚠️ Update may not have been applied');
      console.warn('⚠️ Expected:', outcome.outcomeStatus);
      console.warn('⚠️ Got:', verifyData?.outcome_status);
      return false;
    }

    console.log('✅ Journal outcome updated:', id);
    console.log('✅ New status:', verifyData?.outcome_status);
    return true;
  } catch (error) {
    console.error('❌ Error updating journal outcome:', error);
    return false;
  }
}

/**
 * Delete a journal entry
 */
export async function deleteJournalEntry(id: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('journal_entries')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('❌ Error deleting journal entry:', error);
      return false;
    }

    console.log('✅ Journal entry deleted:', id);
    return true;
  } catch (error) {
    console.error('❌ Error deleting journal entry:', error);
    return false;
  }
}

// =====================================================
// STATS & DISCIPLINE CALCULATION
// =====================================================

/**
 * Get journal statistics for the current user
 * Phase 2.3: Enhanced with Capital Preservation metrics
 */
export async function getJournalStats(days?: number): Promise<JournalStats | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase.rpc('get_journal_stats', {
      p_user_id: user.id,
      p_days: days || null,
    });

    if (error) {
      console.error('❌ Error fetching journal stats:', error);
      return null;
    }

    if (!data || !data.success) {
      return getEmptyStats();
    }

    // Calculate derived metrics
    const wins = data.wins || 0;
    const losses = data.losses || 0;
    const totalCompleted = wins + losses;
    const winRate = totalCompleted > 0 ? (wins / totalCompleted) * 100 : null;
    const totalAnalyses = data.total_analyses || 0;
    const noTradeDecisions = data.no_trade_decisions || 0;
    const waitDecisions = data.wait_decisions || 0;
    const tradeDecisions = data.trade_decisions || 0;
    
    // Phase 2.3: Capital Preservation Metrics
    const capitalPreservationRate = totalAnalyses > 0 
      ? ((noTradeDecisions + waitDecisions) / totalAnalyses) * 100 
      : 0;
    
    // Risk Avoidance Score: Based on regime compliance
    const byRegime = data.by_regime || {};
    const disciplineMetrics = data.discipline_metrics || {};
    const riskAvoidanceScore = calculateRiskAvoidanceScore({
      capitalPreservationRate,
      riskOffCompliance: disciplineMetrics.risk_off_compliance || null,
      avgRMultiple: data.avg_r_multiple,
      winRate,
    });
    
    // Overtrading Detection
    const { overtradingWarning, overtradingLevel } = detectOvertrading({
      tradeDecisions,
      totalAnalyses,
      recentTradeFrequency: tradeDecisions / Math.max(totalAnalyses, 1),
      losses,
      wins,
    });
    
    // Calculate discipline score (includes WAIT decisions)
    const disciplineScore = calculateDisciplineScore({
      tradeDecisions,
      noTradeDecisions,
      waitDecisions,
      wins,
      losses,
      totalAnalyses,
      riskAvoidanceScore,
      overtradingLevel,
    });
    
    // Regime compliance calculation
    const regimeCompliance = calculateRegimeCompliance(byRegime, disciplineMetrics);
    
    // Phase 3: Discipline Drift Detection
    const disciplineDriftResult = calculateDisciplineDrift({
      tradeDecisions,
      noTradeDecisions,
      waitDecisions,
      totalAnalyses,
      losses,
      wins,
      byRegime,
    });

    return {
      totalAnalyses,
      tradeDecisions,
      noTradeDecisions,
      waitDecisions,
      conditionalDecisions: data.conditional_decisions || 0,
      avgConfidence: data.avg_confidence,
      wins,
      losses,
      breakevens: data.breakevens || 0,
      missed: data.missed || 0,
      cancelled: data.cancelled || 0,
      noTradeOutcomes: data.no_trade_outcomes || 0,
      waitOutcomes: data.wait_outcomes || 0,
      pending: data.pending || 0,
      avgRMultiple: data.avg_r_multiple,
      totalRMultiple: data.total_r_multiple,
      winRate,
      disciplineScore,
      byMarket: data.by_market || {},
      byAsset: data.by_asset || {},
      // Phase 2.3 new metrics
      capitalPreservationRate: Math.round(capitalPreservationRate * 10) / 10,
      riskAvoidanceScore,
      overtradingWarning,
      overtradingLevel,
      byRegime,
      regimeCompliance,
      // Phase 3: Discipline Drift
      disciplineDriftLevel: disciplineDriftResult.level,
      disciplineDriftIndicators: disciplineDriftResult.indicators,
      // Phase 3: Risk Committee metrics (placeholder - would need DB columns for actual data)
      avgCapitalProtectionScore: null, // Would need to aggregate from entries
      riskOfficerApprovalRate: null, // Would need to aggregate from entries
    };
  } catch (error) {
    console.error('❌ Error fetching journal stats:', error);
    return null;
  }
}

/**
 * Phase 2.3: Calculate Risk Avoidance Score (0-100)
 * Measures how well the trader avoids unnecessary risk
 */
function calculateRiskAvoidanceScore(params: {
  capitalPreservationRate: number;
  riskOffCompliance: number | null;
  avgRMultiple: number | null;
  winRate: number | null;
}): number {
  const { capitalPreservationRate, riskOffCompliance, avgRMultiple, winRate } = params;
  
  let score = 50; // Start at neutral
  
  // Factor 1: Capital Preservation Rate (40% weight)
  // Ideal range: 20-50% NO_TRADE/WAIT
  if (capitalPreservationRate >= 20 && capitalPreservationRate <= 50) {
    score += 20;
  } else if (capitalPreservationRate >= 15 && capitalPreservationRate <= 60) {
    score += 10;
  } else if (capitalPreservationRate < 10) {
    score -= 15; // Too aggressive
  } else if (capitalPreservationRate > 70) {
    score += 5; // Very conservative (still good)
  }
  
  // Factor 2: Risk-Off Regime Compliance (30% weight)
  if (riskOffCompliance !== null) {
    if (riskOffCompliance >= 80) {
      score += 20; // Excellent - almost always avoids Risk-Off
    } else if (riskOffCompliance >= 60) {
      score += 10;
    } else if (riskOffCompliance < 40) {
      score -= 15; // Trading too much in Risk-Off
    }
  }
  
  // Factor 3: R-Multiple Quality (20% weight)
  if (avgRMultiple !== null) {
    if (avgRMultiple >= 1.5) {
      score += 15;
    } else if (avgRMultiple >= 0.5) {
      score += 5;
    } else if (avgRMultiple < 0) {
      score -= 10;
    }
  }
  
  // Factor 4: Win Rate contribution (10% weight)
  if (winRate !== null) {
    if (winRate >= 55) {
      score += 5;
    } else if (winRate < 40) {
      score -= 5;
    }
  }
  
  return Math.max(0, Math.min(100, Math.round(score)));
}

/**
 * Phase 2.3: Detect overtrading behavior
 */
function detectOvertrading(params: {
  tradeDecisions: number;
  totalAnalyses: number;
  recentTradeFrequency: number;
  losses: number;
  wins: number;
}): { overtradingWarning: boolean; overtradingLevel: 'none' | 'mild' | 'moderate' | 'severe' } {
  const { tradeDecisions, totalAnalyses, recentTradeFrequency, losses, wins } = params;
  
  if (totalAnalyses < 5) {
    return { overtradingWarning: false, overtradingLevel: 'none' };
  }
  
  let warningScore = 0;
  
  // Check 1: Trade frequency too high
  if (recentTradeFrequency > 0.9) {
    warningScore += 3; // Trading on 90%+ of analyses
  } else if (recentTradeFrequency > 0.8) {
    warningScore += 2;
  } else if (recentTradeFrequency > 0.7) {
    warningScore += 1;
  }
  
  // Check 2: More losses than wins
  if (losses > wins && losses >= 3) {
    warningScore += 2;
  }
  
  // Check 3: High volume with negative results
  if (tradeDecisions >= 10 && losses > wins) {
    warningScore += 1;
  }
  
  // Determine level
  let overtradingLevel: 'none' | 'mild' | 'moderate' | 'severe' = 'none';
  if (warningScore >= 5) {
    overtradingLevel = 'severe';
  } else if (warningScore >= 3) {
    overtradingLevel = 'moderate';
  } else if (warningScore >= 1) {
    overtradingLevel = 'mild';
  }
  
  return {
    overtradingWarning: warningScore >= 2,
    overtradingLevel,
  };
}

/**
 * Phase 2.3: Calculate regime compliance percentage
 */
function calculateRegimeCompliance(
  byRegime: Record<string, number>, 
  disciplineMetrics: { risk_off_compliance?: number; capital_preservation_rate?: number }
): number {
  // If we have Risk-Off compliance from DB, use it as primary indicator
  if (disciplineMetrics.risk_off_compliance !== null && disciplineMetrics.risk_off_compliance !== undefined) {
    return Math.round(disciplineMetrics.risk_off_compliance);
  }
  
  // Otherwise calculate from regime data
  const riskOffCount = byRegime['Risk-Off'] || 0;
  const totalWithRegime = Object.values(byRegime).reduce((a, b) => a + b, 0);
  
  if (totalWithRegime === 0) return 100; // No data = assume compliant
  
  // In Risk-Off, we should have high NO_TRADE/WAIT - approximate with overall capital preservation
  return Math.round(disciplineMetrics.capital_preservation_rate || 50);
}

/**
 * Phase 3: Calculate Discipline Drift Detection
 * Detects when trader discipline is degrading before P&L shows it
 */
function calculateDisciplineDrift(params: {
  tradeDecisions: number;
  noTradeDecisions: number;
  waitDecisions: number;
  totalAnalyses: number;
  losses: number;
  wins: number;
  byRegime: Record<string, number>;
}): { 
  level: 'Stable' | 'Drift' | 'Overtrading'; 
  indicators: {
    tradeFrequencyTrend: 'increasing' | 'stable' | 'decreasing';
    riskOffViolations: number;
    consecutiveTrades: number;
    recentLossStreak: number;
  } 
} {
  const { tradeDecisions, noTradeDecisions, waitDecisions, totalAnalyses, losses, wins, byRegime } = params;
  
  // Default indicators
  const indicators = {
    tradeFrequencyTrend: 'stable' as const,
    riskOffViolations: 0,
    consecutiveTrades: 0,
    recentLossStreak: 0,
  };
  
  if (totalAnalyses < 5) {
    return { level: 'Stable', indicators };
  }
  
  let driftScore = 0;
  
  // Factor 1: Trade frequency (high trade % = potential drift)
  const tradeRatio = tradeDecisions / totalAnalyses;
  if (tradeRatio > 0.85) {
    driftScore += 3;
    indicators.tradeFrequencyTrend = 'increasing';
  } else if (tradeRatio > 0.7) {
    driftScore += 1;
    indicators.tradeFrequencyTrend = 'increasing';
  } else if (tradeRatio < 0.4) {
    indicators.tradeFrequencyTrend = 'decreasing';
  }
  
  // Factor 2: Risk-Off violations (trading during Risk-Off regime)
  const riskOffTrades = byRegime['Risk-Off'] || 0;
  if (riskOffTrades > 2) {
    driftScore += 2;
    indicators.riskOffViolations = riskOffTrades;
  } else if (riskOffTrades > 0) {
    driftScore += 1;
    indicators.riskOffViolations = riskOffTrades;
  }
  
  // Factor 3: Loss streak impact
  if (losses > wins + 2) {
    driftScore += 2;
    indicators.recentLossStreak = losses - wins;
  }
  
  // Factor 4: Lack of NO_TRADE/WAIT decisions
  const capitalPreservationRatio = (noTradeDecisions + waitDecisions) / totalAnalyses;
  if (capitalPreservationRatio < 0.1 && totalAnalyses >= 10) {
    driftScore += 2;
  }
  
  // Estimate consecutive trades (approximation based on ratio)
  indicators.consecutiveTrades = Math.round(tradeDecisions * tradeRatio);
  
  // Determine drift level
  let level: 'Stable' | 'Drift' | 'Overtrading' = 'Stable';
  if (driftScore >= 5) {
    level = 'Overtrading';
  } else if (driftScore >= 2) {
    level = 'Drift';
  }
  
  return { level, indicators };
}

/**
 * Calculate discipline score based on trading behavior
 * Phase 2.3: Enhanced with Risk Avoidance and Overtrading factors
 */
function calculateDisciplineScore(params: {
  tradeDecisions: number;
  noTradeDecisions: number;
  waitDecisions?: number;
  wins: number;
  losses: number;
  totalAnalyses: number;
  riskAvoidanceScore?: number;
  overtradingLevel?: 'none' | 'mild' | 'moderate' | 'severe';
}): number {
  const { 
    tradeDecisions, noTradeDecisions, waitDecisions = 0, wins, losses, totalAnalyses,
    riskAvoidanceScore = 50, overtradingLevel = 'none'
  } = params;
  
  if (totalAnalyses === 0) return 100;
  
  let score = 50; // Start at neutral (changed from 100)
  
  // Factor 1: Capital preservation compliance (20% weight)
  const capitalPreservationRatio = totalAnalyses > 0 
    ? (noTradeDecisions + waitDecisions) / totalAnalyses 
    : 0;
  
  if (capitalPreservationRatio >= 0.2 && capitalPreservationRatio <= 0.6) {
    score += 15; // Optimal range
  } else if (capitalPreservationRatio >= 0.15) {
    score += 8;
  } else if (capitalPreservationRatio < 0.1) {
    score -= 10; // Too aggressive
  }
  
  // Factor 2: Risk Avoidance Score contribution (25% weight)
  score += (riskAvoidanceScore - 50) * 0.4; // Scale from -20 to +20
  
  // Factor 3: Win rate impact (25% weight)
  const totalCompleted = wins + losses;
  if (totalCompleted >= 3) {
    const winRate = wins / totalCompleted;
    if (winRate >= 0.6) {
      score += 15;
    } else if (winRate >= 0.5) {
      score += 8;
    } else if (winRate < 0.35) {
      score -= 15;
    }
  }
  
  // Factor 4: Overtrading penalty (20% weight)
  switch (overtradingLevel) {
    case 'severe':
      score -= 20;
      break;
    case 'moderate':
      score -= 12;
      break;
    case 'mild':
      score -= 5;
      break;
  }
  
  // Factor 5: Consistency bonus (10% weight)
  if (totalAnalyses >= 10 && capitalPreservationRatio >= 0.2) {
    score += 5; // Bonus for sustained discipline
  }
  
  // Clamp score between 0 and 100
  return Math.max(0, Math.min(100, Math.round(score)));
}

function getEmptyStats(): JournalStats {
  return {
    totalAnalyses: 0,
    tradeDecisions: 0,
    noTradeDecisions: 0,
    waitDecisions: 0,
    conditionalDecisions: 0,
    avgConfidence: null,
    wins: 0,
    losses: 0,
    breakevens: 0,
    missed: 0,
    cancelled: 0,
    noTradeOutcomes: 0,
    waitOutcomes: 0,
    pending: 0,
    avgRMultiple: null,
    totalRMultiple: null,
    winRate: null,
    disciplineScore: 100,
    byMarket: {},
    byAsset: {},
    // Phase 2.3 metrics
    capitalPreservationRate: 0,
    riskAvoidanceScore: 50,
    overtradingWarning: false,
    overtradingLevel: 'none',
    byRegime: {},
    regimeCompliance: 100,
    // Phase 3 metrics
    disciplineDriftLevel: 'Stable',
    disciplineDriftIndicators: {
      tradeFrequencyTrend: 'stable',
      riskOffViolations: 0,
      consecutiveTrades: 0,
      recentLossStreak: 0,
    },
    avgCapitalProtectionScore: null,
    riskOfficerApprovalRate: null,
  };
}

// Helper function to convert row to entry (inline to avoid import issues)
function rowToJournalEntryFn(row: JournalEntryRow): JournalEntry {
  return {
    id: row.id,
    userId: row.user_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    marketType: row.market_type,
    assetSymbol: row.asset_symbol,
    session: row.session,
    inputPrice: row.input_price,
    aiDecision: {
      executionPermission: row.execution_permission,
      direction: row.direction,
      entry: row.entry_price,
      stopLoss: row.stop_loss,
      takeProfit1: row.take_profit_1,
      takeProfit2: row.take_profit_2,
      takeProfit3: row.take_profit_3,
      riskReward: row.risk_reward,
      confidence: row.confidence,
      // Phase 2.2: Structured justification fields
      marketRegime: (row.market_regime as any) || null,
      primaryReason: row.primary_reason || null,
      invalidationLevel: row.invalidation_level || null,
      reviewTrigger: row.review_trigger || null,
    },
    rawAnalysisText: row.raw_analysis_text,
    metadata: {
      modelUsed: row.model_used,
      chartCount: row.chart_count,
      requestId: row.request_id,
    },
    outcomeStatus: row.outcome_status,
    outcomeRMultiple: row.outcome_r_multiple,
    outcomeNotes: row.outcome_notes,
    outcomeUpdatedAt: row.outcome_updated_at,
    // Advanced features — these were missing here (they existed only in the
    // types/journal.ts mapping) which caused entries loaded via getJournalEntries
    // to lack screenshotUrl. That's why the journal detail modal never rendered
    // the chart thumbnail even when the DB column was populated.
    sessionTag: row.session_tag,
    planTag: row.plan_tag,
    strategyTag: row.strategy_tag,
    screenshotUrl: row.screenshot_url,
    screenshotUrls: Array.isArray((row as any).screenshot_urls) ? (row as any).screenshot_urls : null,
    aiReview: row.ai_review,
  };
}

// Export for use in components
export type { JournalEntry, JournalStats, JournalFilters };
